import type {
  BatchDeclarationGate,
  BatchDeclarationGateResult,
  ExternalToolBatch,
  NestedToolCallIdSets,
  NestedToolCallTimeouts,
  ToolCallExpectedGate,
  ToolCallExpectedGateResult,
  ToolCallExpectedGates,
} from './external-tool-batch.types';

export const BATCH_GATE_TIMEOUT_MS = 5_000;
export const LOST_EXECUTE_TIMEOUT_MS = 30_000;

export const LOST_EXECUTE_TIMEOUT_MESSAGE =
  'Timed out waiting for tool execute';

type StatusGate<TResult extends string> = {
  status: TResult | 'pending';
  waiters: Array<(result: TResult) => void>;
  timeoutId?: ReturnType<typeof setTimeout>;
};

export function getNestedMapValue<T>({
  root,
  batchId,
  toolCallId,
}: {
  root: Map<string, Map<string, T>>;
  batchId: string;
  toolCallId: string;
}): T | undefined {
  return root.get(batchId)?.get(toolCallId);
}

export function setNestedMapValue<T>({
  root,
  batchId,
  toolCallId,
  value,
}: {
  root: Map<string, Map<string, T>>;
  batchId: string;
  toolCallId: string;
  value: T;
}): void {
  const inner = root.get(batchId) ?? new Map<string, T>();

  inner.set(toolCallId, value);
  root.set(batchId, inner);
}

export function deleteNestedMapValue<T>({
  root,
  batchId,
  toolCallId,
}: {
  root: Map<string, Map<string, T>>;
  batchId: string;
  toolCallId: string;
}): void {
  const inner = root.get(batchId);

  if (!inner) {
    return;
  }

  inner.delete(toolCallId);

  if (inner.size === 0) {
    root.delete(batchId);
  }
}

export function hasNestedToolCallId({
  root,
  batchId,
  toolCallId,
}: {
  root: NestedToolCallIdSets;
  batchId: string;
  toolCallId: string;
}): boolean {
  return root.get(batchId)?.has(toolCallId) === true;
}

export function addNestedToolCallId({
  root,
  batchId,
  toolCallId,
}: {
  root: NestedToolCallIdSets;
  batchId: string;
  toolCallId: string;
}): void {
  const inner = root.get(batchId) ?? new Set<string>();

  inner.add(toolCallId);
  root.set(batchId, inner);
}

function releaseStatusGate<TResult extends string>({
  gate,
  result,
  force = false,
}: {
  gate: StatusGate<TResult>;
  result: TResult;
  force?: boolean;
}): void {
  if (!force && gate.status !== 'pending') {
    return;
  }

  if (gate.status === result) {
    return;
  }

  if (gate.timeoutId !== undefined) {
    clearTimeout(gate.timeoutId);
    gate.timeoutId = undefined;
  }

  gate.status = result;
  const waiters = gate.waiters;

  gate.waiters = [];

  for (const waiter of waiters) {
    waiter(result);
  }
}

function awaitStatusGate<TResult extends string>({
  gate,
  timedOutResult,
  timeoutMs,
}: {
  gate: StatusGate<TResult>;
  timedOutResult: TResult;
  timeoutMs: number;
}): Promise<TResult> {
  if (gate.status !== 'pending') {
    return Promise.resolve(gate.status);
  }

  return new Promise((resolve) => {
    gate.waiters.push(resolve);

    if (gate.timeoutId !== undefined) {
      return;
    }

    gate.timeoutId = setTimeout(() => {
      releaseStatusGate({ gate, result: timedOutResult });
    }, timeoutMs);
  });
}

export function getOrCreateBatchDeclarationGate({
  gates,
  batchId,
}: {
  gates: Map<string, BatchDeclarationGate>;
  batchId: string;
}): BatchDeclarationGate {
  const existingGate = gates.get(batchId);

  if (existingGate) {
    return existingGate;
  }

  const gate: BatchDeclarationGate = {
    status: 'pending',
    waiters: [],
  };

  gates.set(batchId, gate);

  return gate;
}

export function getOrCreateToolCallExpectedGate({
  gates,
  batchId,
  toolCallId,
}: {
  gates: ToolCallExpectedGates;
  batchId: string;
  toolCallId: string;
}): ToolCallExpectedGate {
  const existingGate = getNestedMapValue({
    root: gates,
    batchId,
    toolCallId,
  });

  if (existingGate) {
    return existingGate;
  }

  const gate: ToolCallExpectedGate = {
    status: 'pending',
    waiters: [],
  };

  setNestedMapValue({
    root: gates,
    batchId,
    toolCallId,
    value: gate,
  });

  return gate;
}

export function awaitBatchDeclarationGate({
  gates,
  batchId,
}: {
  gates: Map<string, BatchDeclarationGate>;
  batchId: string;
}): Promise<BatchDeclarationGateResult> {
  const gate = getOrCreateBatchDeclarationGate({ gates, batchId });

  return awaitStatusGate({
    gate,
    timedOutResult: 'timedOut',
    timeoutMs: BATCH_GATE_TIMEOUT_MS,
  });
}

export function awaitToolCallExpectedGate({
  gates,
  batchId,
  toolCallId,
}: {
  gates: ToolCallExpectedGates;
  batchId: string;
  toolCallId: string;
}): Promise<ToolCallExpectedGateResult> {
  const gate = getOrCreateToolCallExpectedGate({
    gates,
    batchId,
    toolCallId,
  });

  return awaitStatusGate({
    gate,
    timedOutResult: 'timedOut',
    timeoutMs: BATCH_GATE_TIMEOUT_MS,
  });
}

export function releaseBatchDeclaration({
  gates,
  batchId,
}: {
  gates: Map<string, BatchDeclarationGate>;
  batchId: string;
}): void {
  const gate = getOrCreateBatchDeclarationGate({ gates, batchId });

  releaseStatusGate({ gate, result: 'declared' });
}

export function releaseToolCallExpected({
  gates,
  batchId,
  toolCallId,
}: {
  gates: ToolCallExpectedGates;
  batchId: string;
  toolCallId: string;
}): void {
  const gate = getOrCreateToolCallExpectedGate({
    gates,
    batchId,
    toolCallId,
  });

  releaseStatusGate({ gate, result: 'expected' });
}

export function abortBatchDeclarationGate({
  gates,
  batchId,
  tombstone = false,
}: {
  gates: Map<string, BatchDeclarationGate>;
  batchId: string;
  tombstone?: boolean;
}): void {
  const gate = tombstone
    ? getOrCreateBatchDeclarationGate({ gates, batchId })
    : gates.get(batchId);

  if (!gate) {
    return;
  }

  releaseStatusGate({ gate, result: 'aborted', force: true });

  if (!tombstone) {
    gates.delete(batchId);
  }
}

export function abortToolCallExpectedGates({
  gates,
  batchId,
  tombstone = false,
}: {
  gates: ToolCallExpectedGates;
  batchId: string;
  tombstone?: boolean;
}): void {
  const inner = gates.get(batchId);

  if (!inner) {
    return;
  }

  for (const gate of inner.values()) {
    releaseStatusGate({ gate, result: 'aborted', force: true });
  }

  if (!tombstone) {
    gates.delete(batchId);
  }
}

export function isBatchDeclarationTimedOut({
  gates,
  batchId,
}: {
  gates: Map<string, BatchDeclarationGate>;
  batchId: string;
}): boolean {
  return gates.get(batchId)?.status === 'timedOut';
}

export function isBatchDeclarationAborted({
  gates,
  batchId,
}: {
  gates: Map<string, BatchDeclarationGate>;
  batchId: string;
}): boolean {
  return gates.get(batchId)?.status === 'aborted';
}

export function hasAbortedBatchDeclaration({
  gates,
  batchIds,
}: {
  gates: Map<string, BatchDeclarationGate>;
  batchIds: string[];
}): boolean {
  return batchIds.some((batchId) =>
    isBatchDeclarationAborted({ gates, batchId }),
  );
}

export function isToolCallExpectedTimedOut({
  gates,
  batchId,
  toolCallId,
}: {
  gates: ToolCallExpectedGates;
  batchId: string;
  toolCallId: string;
}): boolean {
  return (
    getNestedMapValue({
      root: gates,
      batchId,
      toolCallId,
    })?.status === 'timedOut'
  );
}

export function isToolCallExpectedAborted({
  gates,
  batchId,
  toolCallId,
}: {
  gates: ToolCallExpectedGates;
  batchId: string;
  toolCallId: string;
}): boolean {
  return (
    getNestedMapValue({
      root: gates,
      batchId,
      toolCallId,
    })?.status === 'aborted'
  );
}

export function armLostExecuteTimer({
  timers,
  batchId,
  toolCallId,
  onLost,
  timeoutMs = LOST_EXECUTE_TIMEOUT_MS,
}: {
  timers: NestedToolCallTimeouts;
  batchId: string;
  toolCallId: string;
  onLost: () => void;
  timeoutMs?: number;
}): void {
  const existingTimeoutId = getNestedMapValue({
    root: timers,
    batchId,
    toolCallId,
  });

  if (existingTimeoutId !== undefined) {
    return;
  }

  const timeoutId = setTimeout(onLost, timeoutMs);

  setNestedMapValue({
    root: timers,
    batchId,
    toolCallId,
    value: timeoutId,
  });
}

export function clearLostExecuteTimer({
  timers,
  batchId,
  toolCallId,
}: {
  timers: NestedToolCallTimeouts;
  batchId: string;
  toolCallId: string;
}): void {
  const timeoutId = getNestedMapValue({
    root: timers,
    batchId,
    toolCallId,
  });

  if (timeoutId === undefined) {
    return;
  }

  clearTimeout(timeoutId);
  deleteNestedMapValue({
    root: timers,
    batchId,
    toolCallId,
  });
}

export function clearLostExecuteTimersForBatch({
  timers,
  batchId,
}: {
  timers: NestedToolCallTimeouts;
  batchId: string;
}): void {
  const inner = timers.get(batchId);

  if (!inner) {
    return;
  }

  for (const timeoutId of inner.values()) {
    clearTimeout(timeoutId);
  }

  timers.delete(batchId);
}

export function syncLostExecuteTimers({
  timers,
  batchId,
  pendingExecuteToolCallIds,
  requestToolCallIds,
  resultToolCallIds,
}: {
  timers: NestedToolCallTimeouts;
  batchId: string;
  pendingExecuteToolCallIds: Set<string>;
  requestToolCallIds: Set<string>;
  resultToolCallIds: Set<string>;
}): void {
  const inner = timers.get(batchId);

  if (!inner) {
    return;
  }

  for (const [toolCallId, timeoutId] of [...inner.entries()]) {
    if (
      pendingExecuteToolCallIds.has(toolCallId) ||
      requestToolCallIds.has(toolCallId) ||
      resultToolCallIds.has(toolCallId)
    ) {
      clearTimeout(timeoutId);
      inner.delete(toolCallId);
    }
  }

  if (inner.size === 0) {
    timers.delete(batchId);
  }
}

export function resetExternalToolChatBatches({
  chatId,
  batches,
  batchDeclarationGates,
  toolCallExpectedGates,
  lostExecuteTimers,
  ignoredExecuteToolCallIds,
  inFlightFlushes,
  inFlightHandlerCounts,
  handlerWorkTokenByBatchId,
}: {
  chatId: string;
  batches: Map<string, ExternalToolBatch>;
  batchDeclarationGates: Map<string, BatchDeclarationGate>;
  toolCallExpectedGates: ToolCallExpectedGates;
  lostExecuteTimers: NestedToolCallTimeouts;
  ignoredExecuteToolCallIds: NestedToolCallIdSets;
  inFlightFlushes: Map<string, Promise<void>>;
  inFlightHandlerCounts: Map<string, number>;
  handlerWorkTokenByBatchId: Map<string, number>;
}): void {
  const batchIds = new Set<string>([chatId]);

  for (const [batchId, batch] of batches.entries()) {
    if (batch.chatId === chatId) {
      batchIds.add(batchId);
    }
  }

  for (const batchId of batchIds) {
    abortBatchDeclarationGate({
      gates: batchDeclarationGates,
      batchId,
      tombstone: batchId !== chatId,
    });
    abortToolCallExpectedGates({
      gates: toolCallExpectedGates,
      batchId,
      tombstone: batchId !== chatId,
    });
    clearLostExecuteTimersForBatch({
      timers: lostExecuteTimers,
      batchId,
    });
    ignoredExecuteToolCallIds.delete(batchId);
    batches.delete(batchId);
    inFlightFlushes.delete(batchId);
    inFlightHandlerCounts.delete(batchId);
    handlerWorkTokenByBatchId.delete(batchId);
  }
}
